<template>
	<header id="header" class="Header" :class="classes" tabindex="-1" @click="handleClick" @keydown.esc.prevent="handleEsc">
		<!-- Identity - logo / app title -->
		<div class="Header-identity">
			<!-- @slot for the identity-->
			<slot name="identity" />
		</div>
		<!-- Main - container for all the header nav stuff -->
		<div class="Header-main">
			<!-- Search -->
			<div v-if="search" class="Header-main-search">
				<div class="Header-main-search-button" tabindex="-1" aria-hidden="true">
					<CloIcon symbol="search" />
					<span class="is-visuallyHidden">{{ $t('nav.search') }}</span>
					<span class="Header-dropdown-closer" data-header-closer></span>
				</div>
				<div id="header-search-panel" class="Header-main-search-panel" tabindex="-1">
					<!-- @slot for mobile search-->
					<slot name="search" />
				</div>
				<!-- @slot for desktop search-->
				<slot name="desktopSearch" />
			</div>
			<!-- Menu - everything inside menu panel on mobile -->
			<div class="Header-main-menu">
				<!-- Mobile menu button - hidden for medium up -->
				<div class="Header-main-menu-button" tabindex="0">
					<CloIcon symbol="menu" />
					<span class="is-visuallyHidden">{{ $t('nav.menu') }}</span>
				</div>
				<!-- Menu panel -->
				<div class="Header-main-menu-panel" tabindex="-1" ref="header-main-menu-panel">
					<!-- Button to close menu panel on mobile -->
					<div class="Header-menu-closer" tabindex="-1" aria-hidden="true" data-header-closer>
						<CloIcon symbol="close" />
						<span>{{ $t('generic.Close') }}</span>
					</div>
					<!-- @slot for the main menu stuff-->
					<slot name="main" />
				</div>
			</div>
		</div>
	</header>
</template>
<script>
import CloIcon from '@/icons/CloIcon/CloIcon.vue';
import CloHeaderIdentity from '@/navigation/CloHeader/CloHeaderIdentity.vue';
import CloHeaderSapsucker from '@/navigation/CloHeader/CloHeaderSapsucker.vue';
import CloHeaderGroup from '@/navigation/CloHeader/CloHeaderGroup.vue';
import CloHeaderList from '@/navigation/CloHeader/CloHeaderList.vue';
import CloHeaderListItem from '@/navigation/CloHeader/CloHeaderListItem.vue';

export default {
	components: { CloIcon },
	name: 'CloHeader',
	props: {
		minimal: {
			type: Boolean,
			default: false,
		},
		noStripe: {
			type: Boolean,
			default: false,
		},
		poweredBy: {
			type: Boolean,
			default: false,
		},
		search: {
			type: Boolean,
			default: false,
		},
	},
	computed: {
		classes: function () {
			const classes = [];
			const prefix = 'Header--';
			if (this.minimal) {
				classes.push(prefix + 'minimal');
			}
			if (this.noStripe) {
				classes.push(prefix + 'noStripe');
			}
			if (this.poweredBy) {
				classes.push(prefix + 'poweredBy');
			}
			return classes.join(' ');
		},
		mainMenuClasses: function () {
			const mainMenuClasses = [];
			const prefix = 'Header-main-menu--';
			if (this.twoRows) {
				mainMenuClasses.push(prefix + 'twoRows');
			}
			return mainMenuClasses.join(' ');
		},
	},
	data() {
		return {};
	},
	methods: {
		handleClick(e) {
			if (e.target.className.indexOf('closer') !== -1) {
				e.stopPropagation();
				e.preventDefault();
				this.$el.focus();
			}
			if (e.target.closest('.Header-group--dropdown') && e.target.nodeName === 'A') {
				this.$el.focus();
			}
		},
		// probably need to rethink this to put focus in correct place
		// depending on mobile/desktop and nesting
		handleEsc() {
			this.$el.focus();
		},
	},
};
</script>

<style lang="scss">
// --------------------------------------------------------------------------
// Header styles
//
// For a11y, nothing in the header or navigation is hidden or display:none
// Instead, things are visually hidden, but still tabable and focusable
// When focused, they are visually shown
// --------------------------------------------------------------------------

// Mixins to set up item colors
// ----------------------------------------------------------------

// Styles for non-link items
@mixin headerItemColor {
	color: $color-text;
}

// Styles for linked items ----------------
@mixin headerLinkColor {
	background: $color-white;
	@include interactiveColor(color, $color-text, $color-text);
}

// Styles for link underlines
@mixin headerLinkUnderline {
	border-inline: 2px none transparent;
	@include interactiveColor(border-color, transparent, $brand-color-red-shade);
	border-style: none;
	border-inline-start-style: solid;
	&[aria-current='true'] {
		@include interactiveColor(border-color, $brand-color-red-shade, $brand-color-red-shade);
	}
}

@mixin headerLinkUnderline--bottom {
	border-inline: none;
	border-block: 2px none transparent;
	border-style: none;
	border-inline-start-style: none;
	border-bottom-style: solid;
	@include interactiveColor(border-color, transparent, $brand-color-red-shade);
}

// Styles for links styled as buttons ----------------
@mixin headerLinkColorButton {
	@include interactiveColor(color, $color-text-reverse, $color-text-reverse);
	@include interactiveColor(background, $brand-color-red-shade, hoverColor($brand-color-red-shade));
}

// Styles for panels ----------------
@mixin headerPanelColor {
	background-color: $brand-color-sand;
}

// Mixins to set up items and links styles
// ----------------------------------------------------------------

// Generic item styles ----------------
@mixin headerItem {
	@include text(4);
	letter-spacing: unset;
	@include inset-sm;
	@include headerItemColor;
	display: inline-flex;
	align-items: center;
	margin-inline-end: $space-xxs;
	.Icon {
		@include icon-md;
		color: $color-text;
	}
	/*
	// Let's get rid of this (causes main dropdown to be too wide)
	& > * {
		&:not(:last-child) {
			@include inline-xs;
		}
	}
	*/
	@include breakpoint(headerBreakpoint) {
		padding: $space-sm 0;
		.Icon {
			width: 1rem;
			height: 1rem;
		}
	}
}

@mixin headerItem--secondary {
	@include breakpoint(headerBreakpoint) {
		@include text(3);
		border-style: none;
	}
}

// Link styles ----------------
@mixin headerLink {
	@include headerLinkColor;
	@include headerLinkUnderline;
}

// Panel styles ----------------
@mixin headerPanel {
	@include dropshadow-2;
	@include headerPanelColor;
	min-width: 12rem;
	display: flex;
	flex-direction: column;
	align-items: stretch;
	.Header-link {
		width: 100%;
		@include stack-xxs;
	}
	li {
		@include stack-xxs;
		.Header-link {
			width: 100%;
		}
	}
}

@mixin headerPanelDropdown {
	@include headerPanel;
	position: absolute;
	inset-block-start: 100%;
	inset-inline-end: 0;
	min-width: 12rem;
}

/* ----------------------------------------------------------------
	Invisible click targets to close panels, added by js
---------------------------------------------------------------- */

@mixin headerDropdownCloser {
	display: none;
	position: absolute;
	top: $space-sm;
	left: 0;
	right: 0;
	bottom: $space-sm;
	margin: 0;
}

/* ----------------------------------------------------------------
	Horizontally adjust text to look more centered with icons (Gibson offset)
---------------------------------------------------------------- */
@mixin adjustTextToIcon {
	// transform: translateY(0.05em);
	transform: translateY(5%);
}

/* ----------------------------------------------------------------
	Core block styles
---------------------------------------------------------------- */

.Header-item {
	@include headerItem;
}

.Header-link {
	@include headerItem;
	@include headerLink;
	&--button {
		@include headerLinkColorButton;
	}
	&.active {
		@include interactiveColor(border-color, $brand-color-red-shade, $brand-color-red-shade);
	}
}

.Header-menu-closer {
	@include headerItem;
}

.Header-search-closer {
	@include headerItem;
}

.Header-group {
	display: flex;
	flex-direction: column;
	@include breakpoint(headerBreakpoint) {
		flex-grow: 1;
		flex-direction: row;
		align-items: center;
		justify-content: flex-end;
		column-gap: $space-md;
		.Header-item,
		.Header-link {
			font-weight: 700;
		}
		.Header-link--button {
			@include headerLinkColorButton;
			padding-inline: 0.75em;
			border-radius: $space-xs;
		}
		&--secondary {
			order: -1;
			flex-grow: 0;
			.Header-item,
			.Header-link {
				@include headerItem--secondary;
				font-weight: unset;
			}
			.Header-link--button {
				align-self: stretch;
				border-radius: 0;
			}
		}
	}
}

.Header-list {
	display: block;
	&[hidden] {
		display: none;
	}
	.Header-list-heading {
		@include headerItem;
		@include text(4);
		margin: 0;
		cursor: pointer;
		font-weight: normal;
	}
	ul.Header-list-list {
		list-style: none;
		margin-inline-start: 0;
		display: flex;
		flex-direction: column;
		li {
			display: flex;
			align-items: center;
		}
	}
	button.Header-link {
		border-radius: 0;
	}
	@include breakpoint(headerBreakpoint) {
		.Header-list-heading {
			@include visuallyHidden;
		}
		ul.Header-list-list {
			flex-direction: row;
			flex-wrap: wrap;
			justify-content: flex-end;
			column-gap: $space-md;
		}
		.Header-link:not(.Header-link--button) {
			@include headerLinkUnderline--bottom;
			&.active {
				@include interactiveColor(border-color, $brand-color-red-shade, $brand-color-red-shade);
			}
		}
	}
	&--dropdown {
		position: relative;
		.Header-list-heading {
			.Icon--triangleDown {
				display: none;
			}
		}
		@include breakpoint(headerBreakpoint) {
			.Header-list-heading {
				@include overrideVisuallyHidden;
				@include headerItem;
				@include headerLink;
				@include headerLinkUnderline--bottom;

				.Header-list-heading-label {
					@include visuallyHidden;
				}
				.Icon {
					width: 1.2rem;
					height: 1.2rem;
				}
				.Icon--triangleDown {
					display: unset;
				}
				// Hidden element to close dropdown by clicking on the heading
				.Header-dropdown-closer {
					@include headerDropdownCloser;
				}
			}
			ul.Header-list-list {
				@include visuallyHidden;
				.Header-item,
				.Header-link {
					padding-inline: $space-sm;
				}
				.Header-link {
					@include headerLinkUnderline();
				}
			}
			&:focus-within {
				ul.Header-list-list {
					@include overrideVisuallyHidden;
					@include headerPanelDropdown;
				}
				.Header-list-heading .Header-dropdown-closer {
					display: inline-block; // unhide closer
				}
			}
		}
	}
}

/* ----------------------------------------------------------------
	Structural styles
---------------------------------------------------------------- */

.Header {
	position: relative;
	background-color: $color-white;
	max-width: 100%;
	z-index: 100;
	display: grid;
	grid-template-columns: auto 1fr;
	column-gap: $space-md;
	grid-template-areas: 'identity	nav';
	border-block-end: 1px solid subdueColor($color-text-muted);
	border-block-start: 4px solid $brand-color-red-shade; // Cornell red racing stripe
	&--noStripe {
		border-block-start: none;
	}
}

.Header-identity {
	display: flex;
}

// Main is a wrapper for all remaining (non-identity) elements
.Header-main {
	display: flex;
	justify-content: flex-end;
	flex-grow: 1;
	@include breakpoint(headerBreakpoint) {
		justify-content: flex-start;
	}
	.Icon + span {
		@include adjustTextToIcon;
	}
}

// Main search contains the mobile search button and search panel
.Header-main-search {
	display: flex;
	align-items: center;
}

.Header-main-search-button {
	@include headerItem;
	position: relative;
	cursor: pointer;
	@include breakpoint(headerBreakpoint) {
		display: none;
	}
	.Header-dropdown-closer {
		@include headerDropdownCloser;
	}
}
.Header-main-search-panel {
	@include visuallyHidden;
	.Header-search-closer {
		width: 100%;
		justify-content: flex-end;
		display: flex;
	}
	@include breakpoint(headerBreakpoint) {
		@include overrideVisuallyHidden;
	}
}
.Header-main-search-button:focus-within + .Header-main-search-panel,
.Header-main-search-panel:focus-within {
	overflow: visible !important;
	@include overrideVisuallyHidden;
	display: flex;
	flex-direction: column;
	@include inset-md;
	@include headerPanelColor;
	@include dropshadow-2;
	z-index: 999;
	position: absolute;
	inset-block-start: 100%; //top
	inset-inline-end: 0; //right
	inset-block-end: auto; //bottom
	inset-inline-start: 0; //left
}
.Header-main-search:focus-within .Header-dropdown-closer {
	display: inline-block;
}

// Main menu contains the mobile menu button and menu panel (everything else)
.Header-main-menu {
	display: flex;
	align-items: stretch;
	justify-content: flex-end;
	@include breakpoint(headerBreakpoint) {
		flex-grow: 1;
	}
	.Header-main-menu-button {
		display: flex;
		gap: $space-xxs;
		@include headerItem;
		padding-inline: $space-md;
		&:focus-within + .Header-main-menu-panel {
			transform: translateX(0%);
		}
		@include breakpoint(headerBreakpoint) {
			display: none;
		}
	}
	.Header-main-menu-panel {
		@include headerPanel;
		overflow: auto;
		display: flex;
		flex-direction: column;
		gap: $space-xs;
		position: fixed;
		inset-block-start: 0; //top
		inset-block-end: 0; //bottom
		inset-inline-end: 0; //right
		transform: translateX(100%);
		[dir='rtl'] & {
			transform: translateX(-100%);
		}
		width: 16rem;
		max-width: calc(100vw - 2rem);
		z-index: 100;
		transition: transform 200ms ease-in-out;
		overflow-block: auto;

		// visible only when focused
		&:focus-within {
			transform: translateX(0%);
		}

		.Header-menu-closer {
			width: 100%;
			justify-content: flex-end;
			display: flex;
			span {
				pointer-events: none;
			}
		}

		@include breakpoint(headerBreakpoint) {
			overflow: visible;
			transform: translateX(0%);
			[dir='rtl'] & {
				transform: translateX(0%);
			}
			width: auto;
			background: none;
			box-shadow: none;
			min-width: unset;
			position: relative;
			overflow-block: visible;
			display: flex;
			flex-direction: column;
			flex-wrap: wrap;
			padding-inline-end: $space-md;
			.Header-menu-closer {
				display: none;
			}
		}
	}
}

// Minimal header (one row)
.Header.Header--minimal {
	border-block-start: none;
	@include breakpoint(headerBreakpoint) {
		.Header-main-menu-panel {
			padding: 0;
			column-gap: $space-md;
			flex-direction: row;
			flex-wrap: nowrap;
			.Header-group .Header-list-list {
				column-gap: 0.75rem;
			}
			.Header-group--secondary {
				order: unset;
				column-gap: $space-sm;
			}
			.Header-link {
				padding-block: $space-xs;
			}
			.Header-link--button {
				border-radius: $space-xs;
				padding: 0.25em 0.5em;
			}
			.HeaderSapsucker-button img {
				height: 2rem;
			}
		}
	}
}
</style>
